import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, Wrench, Car, User, AlertTriangle, ChevronLeft, ChevronRight, Loader2, Plus, Trash2, Clock } from 'lucide-react';
import { formatAppointmentDateTime } from '../services/dateService';
import { api } from '../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Labels/verbs for each server-resolved action. The action itself is never decided
// here - it comes from `lead.manualAction` (ManualAppointmentResolver on the
// backend) so the button can't claim to do something different from what the
// endpoint will actually run.
const ACTION_COPY = {
  BOOK: {
    title: 'Book appointment',
    submit: 'Book',
    blurb: '',
  },
  REBOOK: {
    title: 'Re-book appointment',
    submit: 'Re-book',
    blurb:
      'This appointment was canceled. Re-booking clears the old Ztime references and creates a new appointment.',
  },
  RESCHEDULE: {
    title: 'Reschedule appointment',
    submit: 'Reschedule',
    blurb: 'Moves the existing Ztime appointment to a new date/time.',
  },
};

// LeadAppointment stores naive wall-clock (store-local) datetimes, and
// dateService renders them verbatim. So the value of a <input type="datetime-local">
// maps straight across in both directions - deliberately no `new Date()` round-trip,
// which would shift the time by the browser's offset.
const toInputValue = (isoish) => {
  if (!isoish) return '';
  const match = String(isoish).trim().match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}` : '';
};

const toPayloadValue = (inputValue) => {
  if (!inputValue) return null;
  // datetime-local gives "YYYY-MM-DDTHH:mm"; the backend parses LocalDateTime, which
  // accepts seconds-less ISO, but be explicit so it round-trips identically.
  return inputValue.length === 16 ? `${inputValue}:00` : inputValue;
};

const formatJsonPayload = (payload) => {
  if (!payload) return '';
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch (e) {
    return payload;
  }
};

// Date -> "YYYY-MM-DDTHH:mm" (datetime-local shape, local wall clock).
const toLocalInputValue = (date) => {
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
    {children}
  </label>
);

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent';

const ManualAppointmentModal = ({ lead, onClose, onSubmit }) => {
  const appointment = lead.appointment || {};
  const action = lead.manualAction;
  const copy = ACTION_COPY[action] || ACTION_COPY.BOOK;

  const [requestedDateTime, setRequestedDateTime] = useState(toInputValue(appointment.requestedDateTime));
  const [service, setService] = useState(appointment.service || '');
  const [opcode, setOpcode] = useState(appointment.opcode || '');
  const [transportationType, setTransportationType] = useState(appointment.transportationType || '');
  const [advisor, setAdvisor] = useState(appointment.advisor || '');
  // "N"/"Y" - not persisted on LeadAppointment, so no stored value to seed from; defaults to
  // "N" (not waiting). Ztime's reschedule endpoint 500s server-side on a null waiter, so the
  // backend also defaults this if it's ever sent blank - this is belt and suspenders.
  const [waiter, setWaiter] = useState(appointment.waiter || 'N');
  const [notes, setNotes] = useState('Appointment booked by AI');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Lets the agent correct an already-attached customer's name (e.g. a DMS phone-number match
  // brought in the wrong person). Hidden by default so the common case - name's already right -
  // stays a single read-only line.
  const [editingName, setEditingName] = useState(false);
  // "Check availability" preview: asks N2's datetime_template_response endpoint
  // whether the picked slot works, then shows the returned message plus the raw
  // N2 request/response behind the N2 button (same viewer as the chat widget).
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [showAvailabilityN2, setShowAvailabilityN2] = useState(false);
  const [isSendingAvailability, setIsSendingAvailability] = useState(false);
  const [availabilitySent, setAvailabilitySent] = useState(false);
  const [availabilitySendError, setAvailabilitySendError] = useState(null);
  // The picker edits pendingDate; OK inside the picker commits it to
  // requestedDateTime (the value used by booking and the availability check),
  // Cancel reverts. Both close the popper - no clicking outside needed.
  const [pendingDate, setPendingDate] = useState(() =>
    requestedDateTime ? new Date(requestedDateTime) : null
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  // Modal-level tabs: 'booking' or 'availability'
  const [modalTab, setModalTab] = useState('booking');
  // Availability mode: one of the 5 exclusive modes
  const [availabilityMode, setAvailabilityMode] = useState('time'); // 'time', 'timeRange', 'dateRange', 'freeText', 'daySlots'
  const [complexOptions, setComplexOptions] = useState({
    timeOption: { qualifier: 'after', time: '' }, // Time-only (HH:mm format)
    timeRange: '',
    dateRangeOption: { qualifier: 'next', period: 'week' },
    freeTextDateTime: '',
    daySlots: [], // Each slot is now a full datetime string (ISO format)
  });
  const [isCheckingComplexAvailability, setIsCheckingComplexAvailability] = useState(false);
  // Advisor and transportType for availability check (moved here from booking form)
  const [availabilityAdvisor, setAvailabilityAdvisor] = useState('');
  const [availabilityTransportType, setAvailabilityTransportType] = useState('');
  // Date picker state for day slots
  const [daySlotPickerOpen, setDaySlotPickerOpen] = useState({});
  // Time picker state for Time mode
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [tempTime, setTempTime] = useState('');
  const timePickerRef = useRef(null);

  // Close time picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
        setTimePickerOpen(false);
      }
    };

    if (timePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [timePickerOpen]);

  // Which of customer/vehicle the lead doesn't have yet, resolved server-side. When listed, the
  // agent types them here and the backend creates + attaches them before booking - that's what
  // makes booking possible at the very start of a conversation.
  const requiredInputs = lead.manualActionRequiredInputs || [];
  const needsCustomerInput = requiredInputs.includes('customer');
  const needsVehicleInput = requiredInputs.includes('vehicle');

  const splitName = (lead.customerName && lead.customerName !== 'Customer Pending'
    ? lead.customerName
    : ''
  ).trim().split(/\s+/).filter(Boolean);

  const [customerFirstName, setCustomerFirstName] = useState(splitName[0] || '');
  const [customerLastName, setCustomerLastName] = useState(splitName.slice(1).join(' ') || '');
  const [vehicleMake, setVehicleMake] = useState(lead.vehicle?.make || '');
  const [vehicleModel, setVehicleModel] = useState(lead.vehicle?.model || '');
  const [vehicleYear, setVehicleYear] = useState(lead.vehicle?.year ? String(lead.vehicle.year) : '');
  const [vehicleVin, setVehicleVin] = useState(lead.vehicle?.vin || '');

  const vehicleLabel = lead.vehicle
    ? [lead.vehicle.year, lead.vehicle.make, lead.vehicle.model].filter(Boolean).join(' ')
    : null;

  // Ztime needs all four of these. Customer and vehicle aren't editable here - they come from the
  // lead's selected DMS customer/vehicle - so when either is missing the fix is DMS Lookup, not this
  // form. The backend blocks the same cases (ManualAppointmentResolver returns NONE, so the button
  // shouldn't even render); this is the second line of defence and, more usefully, tells the agent
  // exactly what's missing. "Customer Pending" is LeadApiService's placeholder for "no customer".
  const hasCustomerName = needsCustomerInput
    ? Boolean(customerFirstName.trim() || customerLastName.trim())
    : Boolean(lead.customerName) && lead.customerName.trim() && lead.customerName !== 'Customer Pending';
  // Ztime validates the vehicle by model + year, so a make alone isn't enough.
  const hasVehicle = needsVehicleInput
    ? Boolean(vehicleModel.trim() && vehicleYear.trim())
    : Boolean(lead.vehicle && lead.vehicle.model && lead.vehicle.year);
  const hasService = Boolean(service.trim());
  const hasOpcode = Boolean(opcode.trim());
  const hasNotes = Boolean(notes.trim());
  const hasDateTime = Boolean(requestedDateTime);

  const missingRequired = [
    !hasCustomerName && 'customer name',
    !hasVehicle && 'vehicle (model and year)',
    !hasService && 'service',
    !hasOpcode && 'op code',
    !hasNotes && 'message / reason',
    !hasDateTime && 'date & time',
  ].filter(Boolean);

  const canSubmit = missingRequired.length === 0 && !isSubmitting;

  const RequiredMark = ({ ok }) => (
    <span className={ok ? 'text-green-600' : 'text-red-500'} title={ok ? 'Provided' : 'Required'}>
      {ok ? '✓' : '*'}
    </span>
  );

  const handleCheckAvailability = async () => {
    if (!pendingDate) {
      setAvailabilityResult({ success: false, message: 'Pick a date & time first.' });
      return;
    }
    // Whatever is in the picker is what gets checked - commit it so booking uses it too.
    const committed = toLocalInputValue(pendingDate);
    setRequestedDateTime(committed);
    setIsCheckingAvailability(true);
    setAvailabilityResult(null);
    setAvailabilitySent(false);
    setAvailabilitySendError(null);
    try {
      const response = await api.post(`/appointments/${lead.id}/check-availability`, {
        dateTime: toPayloadValue(committed),
        transportType: transportationType.trim() || null,
        advisor: advisor.trim() || null,
      });
      const data = response.data || {};
      setAvailabilityResult({
        success: Boolean(data.success),
        message: data.assistantMessage || data.error || 'No availability message returned.',
        n2RequestJson: data.n2RequestJson || null,
        n2ResponseJson: data.n2ResponseJson || null,
      });
    } catch (err) {
      setAvailabilityResult({
        success: false,
        message: err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Availability check failed.',
      });
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  // Sends the N2-generated availability message to the customer as an outbound
  // text (simulated unless the lead has real-SMS enabled), keeping the N2
  // payloads attached to the saved message.
  const handleSendAvailability = async () => {
    if (!availabilityResult?.message || isSendingAvailability || availabilitySent) return;
    setIsSendingAvailability(true);
    setAvailabilitySendError(null);
    try {
      await api.post(`/appointments/${lead.id}/send-availability`, {
        message: availabilityResult.message,
        n2RequestJson: availabilityResult.n2RequestJson || null,
        n2ResponseJson: availabilityResult.n2ResponseJson || null,
      });
      setAvailabilitySent(true);
    } catch (err) {
      setAvailabilitySendError(
        err?.response?.data?.message || err?.message || 'Failed to send availability message.'
      );
    } finally {
      setIsSendingAvailability(false);
    }
  };

  const handleCheckComplexAvailability = async () => {
    // Validate based on selected mode
    let hasOption = false;
    switch (availabilityMode) {
      case 'time':
        hasOption = complexOptions.timeOption && complexOptions.timeOption.time;
        break;
      case 'timeRange':
        hasOption = complexOptions.timeRange;
        break;
      case 'dateRange':
        hasOption = complexOptions.dateRangeOption && complexOptions.dateRangeOption.period;
        break;
      case 'freeText':
        hasOption = complexOptions.freeTextDateTime;
        break;
      case 'daySlots':
        hasOption = complexOptions.daySlots && complexOptions.daySlots.some(slot => slot);
        break;
    }

    if (!hasOption) {
      setAvailabilityResult({ success: false, message: 'Please provide the required information for the selected mode.' });
      return;
    }

    setIsCheckingComplexAvailability(true);
    setAvailabilitySent(false);
    setAvailabilitySendError(null);

    try {
      const response = await api.post(`/appointments/${lead.id}/check-availability-complex`, {
        timeOption: availabilityMode === 'time' ? {
          ...complexOptions.timeOption,
          time: complexOptions.timeOption.time
        } : null,
        timeRange: availabilityMode === 'timeRange' ? complexOptions.timeRange : null,
        dateRangeOption: availabilityMode === 'dateRange' ? complexOptions.dateRangeOption : null,
        freeTextDateTime: availabilityMode === 'freeText' ? complexOptions.freeTextDateTime : null,
        daySlots: availabilityMode === 'daySlots' ? complexOptions.daySlots.filter(slot => slot).map(slot => toPayloadValue(slot)) : null,
        transportType: availabilityTransportType.trim() || null,
        advisor: availabilityAdvisor.trim() || null,
      });
      const data = response.data || {};
      setAvailabilityResult({
        success: Boolean(data.success),
        message: data.assistantMessage || data.error || 'No availability message returned.',
        n2RequestJson: data.n2RequestJson || null,
        n2ResponseJson: data.n2ResponseJson || null,
      });
    } catch (err) {
      setAvailabilityResult({
        success: false,
        message: err?.response?.data?.assistantMessage || err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Availability check failed.',
        n2RequestJson: err?.response?.data?.n2RequestJson || null,
        n2ResponseJson: err?.response?.data?.n2ResponseJson || null,
      });
    } finally {
      setIsCheckingComplexAvailability(false);
    }
  };

  const addDaySlot = () => {
    setComplexOptions(prev => ({
      ...prev,
      daySlots: [...prev.daySlots, ''], // Empty string for new slot
    }));
  };

  const removeDaySlot = (index) => {
    setComplexOptions(prev => ({
      ...prev,
      daySlots: prev.daySlots.filter((_, i) => i !== index),
    }));
  };

  const updateDaySlot = (index, value) => {
    setComplexOptions(prev => ({
      ...prev,
      daySlots: prev.daySlots.map((slot, i) =>
        i === index ? value : slot
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (missingRequired.length > 0) {
      setError(`Missing required field(s): ${missingRequired.join(', ')}.`);
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await onSubmit(lead.id, {
        expectedAction: action,
        appointmentVersion: lead.manualActionVersion ?? null,
        requestedDateTime: toPayloadValue(requestedDateTime),
        service: service.trim() || null,
        opcode: opcode.trim() || null,
        customerFirstName: customerFirstName.trim() || null,
        customerLastName: customerLastName.trim() || null,
        vehicleMake: needsVehicleInput ? vehicleMake.trim() || null : null,
        vehicleModel: needsVehicleInput ? vehicleModel.trim() || null : null,
        vehicleYear: needsVehicleInput && vehicleYear.trim() ? Number(vehicleYear.trim()) : null,
        vehicleVin: needsVehicleInput ? vehicleVin.trim() || null : null,
        transportationType: transportationType.trim() || null,
        advisor: advisor.trim() || null,
        waiter,
        notes: notes.trim() || null,
      });

      if (result?.ok) {
        onClose();
      } else {
        // Keep the modal open with the agent's input intact so they can fix a field
        // and retry - a Ztime rejection usually names exactly what was wrong.
        setError(result?.message || 'The appointment action failed.');
      }
    } catch (err) {
      setError(err?.message || 'The appointment action failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl h-[85vh] flex flex-col relative">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">{copy.title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{copy.blurb}</p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Modal-level tabs */}
        <div className="px-5 pt-4 pb-4 bg-gray-50">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setModalTab('booking')}
              className={`text-sm font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 ${
                modalTab === 'booking'
                  ? 'bg-blue-600 text-white shadow-sm ring-2 ring-blue-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
              }`}
            >
              Booking
            </button>
            <button
              type="button"
              onClick={() => setModalTab('availability')}
              className={`text-sm font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 ${
                modalTab === 'availability'
                  ? 'bg-blue-600 text-white shadow-sm ring-2 ring-blue-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
              }`}
            >
              Availability Check
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          {modalTab === 'booking' && (
            <>
              {/* Error message */}
              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Missing required fields warning */}
              {missingRequired.length > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>
                    Still needed: <span className="font-semibold">{missingRequired.join(', ')}</span>.
                    {(!hasCustomerName || !hasVehicle) && !needsCustomerInput && !needsVehicleInput &&
                      ' Customer and vehicle come from the DMS record - use DMS Lookup on the conversation to attach them.'}
                  </span>
                </div>
              )}

              {/* Read-only context: what this booking will be sent as. */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 flex flex-col gap-2">
            {!needsCustomerInput && (
              <div className="flex items-center justify-between gap-2 text-sm text-gray-700">
                <div className="flex items-center gap-2 min-w-0">
                  <User size={14} className="text-gray-400 shrink-0" />
                  <RequiredMark ok={hasCustomerName} />
                  <span className="font-medium truncate">{lead.customerName}</span>
                  {lead.customerPhone && <span className="text-gray-400 shrink-0">· {lead.customerPhone}</span>}
                </div>
                {!editingName && (
                  <button
                    type="button"
                    onClick={() => setEditingName(true)}
                    className="text-blue-600 hover:underline shrink-0"
                  >
                    Change name
                  </button>
                )}
              </div>
            )}
            {!needsCustomerInput && editingName && (
              <div className="grid grid-cols-2 gap-4 pt-1">
                <Field label="First name">
                  <input
                    type="text"
                    value={customerFirstName}
                    onChange={(e) => setCustomerFirstName(e.target.value)}
                    className={inputClass}
                    placeholder="First"
                  />
                </Field>
                <Field label="Last name">
                  <input
                    type="text"
                    value={customerLastName}
                    onChange={(e) => setCustomerLastName(e.target.value)}
                    className={inputClass}
                    placeholder="Last"
                  />
                </Field>
              </div>
            )}
            {needsCustomerInput && lead.customerPhone && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <User size={14} className="text-gray-400 shrink-0" />
                <span className="text-gray-500">New customer · {lead.customerPhone}</span>
              </div>
            )}
            {!needsVehicleInput && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Car size={14} className="text-gray-400 shrink-0" />
                <RequiredMark ok={hasVehicle} />
                <span>{vehicleLabel}</span>
                {lead.vehicle?.vin && <span className="text-gray-400 truncate">· VIN {lead.vehicle.vin}</span>}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Calendar size={14} className="text-gray-400 shrink-0" />
              <span>
                Current status: <span className="font-medium">{appointment.status || 'none'}</span>
                {appointment.confirmedDateTime
                  ? ` · confirmed ${formatAppointmentDateTime(appointment.confirmedDateTime)}`
                  : appointment.requestedDateTime
                    ? ` · ${formatAppointmentDateTime(appointment.requestedDateTime)}`
                    : ''}
                {appointment.pendingChange && appointment.requestedDateTime
                  ? ` · requested ${formatAppointmentDateTime(appointment.requestedDateTime)}`
                  : ''}
              </span>
            </div>
            {(appointment.appointmentRef || appointment.externalAppointmentId) && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Wrench size={14} className="text-gray-400 shrink-0" />
                <span className="truncate">
                  Ztime ref {appointment.appointmentRef || '-'} · id{' '}
                  {appointment.externalAppointmentId || '-'}
                </span>
              </div>
            )}
          </div>

          {/* Why the last attempt failed, when there was one. */}
          {appointment.errorMessage && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                <span className="font-semibold">Last attempt failed:</span> {appointment.errorMessage}
              </span>
            </div>
          )}

          {needsCustomerInput && (
            <div className="flex flex-col gap-4 border border-blue-100 bg-blue-50/40 rounded-xl px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-800">
                New customer <RequiredMark ok={hasCustomerName} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="First name">
                  <input
                    type="text"
                    value={customerFirstName}
                    onChange={(e) => setCustomerFirstName(e.target.value)}
                    className={inputClass}
                    placeholder="First"
                  />
                </Field>
                <Field label="Last name">
                  <input
                    type="text"
                    value={customerLastName}
                    onChange={(e) => setCustomerLastName(e.target.value)}
                    className={inputClass}
                    placeholder="Last"
                  />
                </Field>
              </div>
            </div>
          )}

          {needsVehicleInput && (
            <div className="flex flex-col gap-4 border border-blue-100 bg-blue-50/40 rounded-xl px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-800">
                Vehicle <RequiredMark ok={hasVehicle} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Make">
                  <input
                    type="text"
                    value={vehicleMake}
                    onChange={(e) => setVehicleMake(e.target.value)}
                    className={inputClass}
                    placeholder="Toyota"
                  />
                </Field>
                <Field label="Model *">
                  <input
                    type="text"
                    value={vehicleModel}
                    onChange={(e) => setVehicleModel(e.target.value)}
                    className={inputClass}
                    placeholder="Camry"
                    required
                  />
                </Field>
                <Field label="Year *">
                  <input
                    type="number"
                    value={vehicleYear}
                    onChange={(e) => setVehicleYear(e.target.value)}
                    className={inputClass}
                    placeholder="2021"
                    min="1900"
                    max="2100"
                    required
                  />
                </Field>
              </div>
              <Field label="VIN (optional)">
                <input
                  type="text"
                  value={vehicleVin}
                  onChange={(e) => setVehicleVin(e.target.value)}
                  className={inputClass}
                  placeholder="17-character VIN"
                />
              </Field>
            </div>
          )}

          {/* Date & time for booking */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Date &amp; time <RequiredMark ok={hasDateTime} />
            </span>
            <div className="flex items-center gap-3">
              <DatePicker
                selected={pendingDate}
                onChange={(date) => setPendingDate(date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="dd/MM/yyyy HH:mm"
                placeholderText="Select date & time"
                open={pickerOpen}
                onInputClick={() => setPickerOpen(true)}
                onClickOutside={() => setPickerOpen(false)}
                shouldCloseOnSelect={false}
                className={inputClass}
                wrapperClassName="w-full"
                popperProps={{ strategy: 'fixed', zIndex: 70 }}
              >
                <div className="flex justify-end gap-2 border-t border-gray-100 mt-1 px-2 pt-2 pb-1">
                  <button
                    type="button"
                    onClick={() => {
                      setPendingDate(requestedDateTime ? new Date(requestedDateTime) : null);
                      setPickerOpen(false);
                    }}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!pendingDate}
                    onClick={() => {
                      setRequestedDateTime(toLocalInputValue(pendingDate));
                      setPickerOpen(false);
                    }}
                    className="text-xs font-semibold px-4 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    OK
                  </button>
                </div>
              </DatePicker>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label={<>Service <RequiredMark ok={hasService} /></>}>
              <input
                type="text"
                value={service}
                onChange={(e) => setService(e.target.value)}
                placeholder="e.g. Oil change"
                className={inputClass}
                required
              />
            </Field>
            <Field label={<>Op code <RequiredMark ok={hasOpcode} /></>}>
              <input
                type="text"
                value={opcode}
                onChange={(e) => setOpcode(e.target.value)}
                placeholder="e.g. LOF"
                className={inputClass}
                required
              />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Transport">
              <select
                value={transportationType}
                onChange={(e) => setTransportationType(e.target.value)}
                className={inputClass}
              >
                <option value="">Dropoff (default)</option>
                <option value="Dropoff">Dropoff</option>
                <option value="Waiter">Waiter</option>
                <option value="Loaner">Loaner</option>
                <option value="Pickup">Pickup</option>
              </select>
            </Field>
            <Field label="Advisor">
              <input
                type="text"
                value={advisor}
                onChange={(e) => setAdvisor(e.target.value)}
                placeholder="Advisor id"
                className={inputClass}
              />
            </Field>
            <Field label="Waiting on-site?">
              <select
                value={waiter}
                onChange={(e) => setWaiter(e.target.value)}
                className={inputClass}
              >
                <option value="N">No (default)</option>
                <option value="Y">Yes</option>
              </select>
            </Field>
          </div>

          <Field label={<>Notes <RequiredMark ok={hasNotes} /></>}>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputClass}
              required
            />
          </Field>
            </>
          )}

          {modalTab === 'availability' && (
            <>
              {/* Advisor and Transport Type for availability check */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Transport">
                  <select
                    value={availabilityTransportType}
                    onChange={(e) => setAvailabilityTransportType(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Dropoff (default)</option>
                    <option value="Dropoff">Dropoff</option>
                    <option value="Waiter">Waiter</option>
                    <option value="Loaner">Loaner</option>
                    <option value="Pickup">Pickup</option>
                  </select>
                </Field>
                <Field label="Advisor">
                  <input
                    type="text"
                    value={availabilityAdvisor}
                    onChange={(e) => setAvailabilityAdvisor(e.target.value)}
                    placeholder="Advisor id"
                    className={inputClass}
                  />
                </Field>
              </div>

              {/* 5 exclusive mode sub-tabs */}
              <div className="flex flex-col gap-3">
                <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Availability Mode
                </span>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { id: 'time', label: 'Time' },
                    { id: 'timeRange', label: 'Time Range' },
                    { id: 'dateRange', label: 'Date Range' },
                    { id: 'freeText', label: 'Free Text' },
                    { id: 'daySlots', label: 'Day Slots' },
                  ].map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => setAvailabilityMode(mode.id)}
                      className={`text-xs font-semibold px-3 py-2.5 rounded-lg transition-all duration-200 ${
                        availabilityMode === mode.id
                          ? 'bg-blue-600 text-white shadow-sm ring-2 ring-blue-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mode-specific content */}
              <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                {availabilityMode === 'time' && (
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-600">Time Option</span>
                    <div className="flex gap-3">
                      <select
                        value={complexOptions.timeOption.qualifier}
                        onChange={(e) => setComplexOptions(prev => ({
                          ...prev,
                          timeOption: { ...prev.timeOption, qualifier: e.target.value }
                        }))}
                        className={`${inputClass} flex-1`}
                      >
                        <option value="after">After</option>
                        <option value="before">Before</option>
                      </select>
                      <div className="relative flex-1">
                        <div
                          onClick={() => {
                            setTempTime(complexOptions.timeOption.time);
                            setTimePickerOpen(true);
                          }}
                          className={`${inputClass} cursor-pointer flex items-center justify-between`}
                        >
                          <span className={complexOptions.timeOption.time ? '' : 'text-gray-400'}>
                            {complexOptions.timeOption.time || 'Select time'}
                          </span>
                          <Clock size={16} className="text-gray-400" />
                        </div>
                        {timePickerOpen && (
                          <div ref={timePickerRef} className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-[100] w-32">
                            <div className="max-h-32 overflow-y-auto p-1">
                              {Array.from({ length: 24 }, (_, hour) =>
                                ['00', '15', '30', '45'].map((minute) => {
                                  const timeString = `${hour.toString().padStart(2, '0')}:${minute}`;
                                  return (
                                    <button
                                      key={timeString}
                                      type="button"
                                      onClick={() => setTempTime(timeString)}
                                      className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                                        tempTime === timeString
                                          ? 'bg-blue-600 text-white'
                                          : 'hover:bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      {timeString}
                                    </button>
                                  );
                                })
                              )}
                            </div>
                            <div className="flex justify-end p-2 pt-1 border-t border-gray-100">
                              <button
                                type="button"
                                disabled={!tempTime}
                                onClick={() => {
                                  setComplexOptions(prev => ({
                                    ...prev,
                                    timeOption: { ...prev.timeOption, time: tempTime }
                                  }));
                                  setTimePickerOpen(false);
                                }}
                                className="text-xs font-semibold px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                OK
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {availabilityMode === 'timeRange' && (
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-600">Time Range</span>
                    <select
                      value={complexOptions.timeRange}
                      onChange={(e) => setComplexOptions(prev => ({ ...prev, timeRange: e.target.value }))}
                      className={inputClass}
                    >
                      <option value="">Select time range</option>
                      <option value="morning">Morning</option>
                      <option value="afternoon">Afternoon</option>
                      <option value="evening">Evening</option>
                    </select>
                  </div>
                )}

                {availabilityMode === 'dateRange' && (
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-600">Date Range</span>
                    <div className="flex gap-3">
                      <select
                        value={complexOptions.dateRangeOption.qualifier}
                        onChange={(e) => setComplexOptions(prev => ({
                          ...prev,
                          dateRangeOption: { ...prev.dateRangeOption, qualifier: e.target.value }
                        }))}
                        className={`${inputClass} flex-1`}
                      >
                        <option value="next">Next</option>
                        <option value="coming">Coming</option>
                        <option value="after">After</option>
                        <option value="before">Before</option>
                        <option value="this">This</option>
                      </select>
                      <select
                        value={complexOptions.dateRangeOption.period}
                        onChange={(e) => setComplexOptions(prev => ({
                          ...prev,
                          dateRangeOption: { ...prev.dateRangeOption, period: e.target.value }
                        }))}
                        className={`${inputClass} flex-1`}
                      >
                        <option value="week">Week</option>
                        <option value="weekend">Weekend</option>
                        <option value="weekday">Weekday</option>
                        <option value="month">Month</option>
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                    </div>
                  </div>
                )}

                {availabilityMode === 'freeText' && (
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-600">Free Text Date/Time</span>
                    <input
                      type="text"
                      value={complexOptions.freeTextDateTime}
                      onChange={(e) => setComplexOptions(prev => ({ ...prev, freeTextDateTime: e.target.value }))}
                      placeholder="e.g., next Tuesday at 3pm"
                      className={inputClass}
                    />
                  </div>
                )}

                {availabilityMode === 'daySlots' && (
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-600">Day Slots</span>
                    {complexOptions.daySlots.map((slot, index) => (
                      <div key={index} className="flex gap-3 items-center">
                        <DatePicker
                          selected={slot ? new Date(slot) : null}
                          onChange={(date) => updateDaySlot(index, date ? toLocalInputValue(date) : '')}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="dd/MM/yyyy HH:mm"
                          placeholderText="Select date & time"
                          open={daySlotPickerOpen[index]}
                          onInputClick={() => setDaySlotPickerOpen(prev => ({ ...prev, [index]: true }))}
                          onClickOutside={() => setDaySlotPickerOpen(prev => ({ ...prev, [index]: false }))}
                          shouldCloseOnSelect={false}
                          className={`${inputClass} flex-1`}
                          wrapperClassName="flex-1"
                          popperProps={{ strategy: 'fixed', zIndex: 70 }}
                        >
                          <div className="flex justify-end gap-2 border-t border-gray-100 mt-1 px-2 pt-2 pb-1">
                            <button
                              type="button"
                              onClick={() => {
                                updateDaySlot(index, '');
                                setDaySlotPickerOpen(prev => ({ ...prev, [index]: false }));
                              }}
                              className="text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              disabled={!slot}
                              onClick={() => setDaySlotPickerOpen(prev => ({ ...prev, [index]: false }))}
                              className="text-xs font-semibold px-4 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              OK
                            </button>
                          </div>
                        </DatePicker>
                        <button
                          type="button"
                          onClick={() => removeDaySlot(index)}
                          className="shrink-0 p-2.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove day slot"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addDaySlot}
                      className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 py-2.5 px-4 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Plus size={16} />
                      Add day slot
                    </button>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={handleCheckComplexAvailability}
                disabled={isCheckingComplexAvailability}
                className="flex items-center justify-center gap-1.5 text-sm font-semibold px-6 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 shadow-sm"
              >
                {isCheckingComplexAvailability && <Loader2 size={14} className="animate-spin" />}
                Check availability
              </button>

              {/* Availability result box - always visible as placeholder */}
              <div
                className={`flex flex-col gap-2 rounded-xl border px-5 py-4 min-h-[100px] ${
                  availabilityResult
                    ? availabilityResult.success
                      ? 'border-green-200 bg-green-50/60'
                      : 'border-amber-200 bg-amber-50/60'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm font-semibold uppercase tracking-wide ${
                      availabilityResult
                        ? availabilityResult.success
                          ? 'text-green-800'
                          : 'text-amber-800'
                        : 'text-gray-500'
                    }`}
                  >
                    Availability
                  </span>
                  {availabilityResult && (
                    <div className="flex items-center gap-2">
                      {availabilityResult.message && (
                        <button
                          type="button"
                          onClick={handleSendAvailability}
                          disabled={isSendingAvailability || availabilitySent}
                          className={`text-sm font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow text-white disabled:opacity-60 ${
                            availabilitySent ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                          title={availabilitySent ? 'Message sent' : 'Send this message to the customer'}
                        >
                          {isSendingAvailability && <Loader2 size={12} className="animate-spin" />}
                          {availabilitySent ? 'Sent' : 'Send'}
                        </button>
                      )}
                      {(availabilityResult.n2RequestJson || availabilityResult.n2ResponseJson) && (
                        <button
                          type="button"
                          onClick={() => setShowAvailabilityN2(true)}
                          className="bg-gray-700 hover:bg-gray-800 text-white text-sm px-3 py-1.5 rounded-full flex items-center shadow"
                          title="View N2 request/response"
                        >
                          <ChevronLeft size={10} className="mr-1" />
                          <ChevronRight size={10} className="-ml-1.5" />
                          N2
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {availabilityResult ? (
                  <p
                    className={`text-sm whitespace-pre-wrap ${
                      availabilityResult.success ? 'text-green-900' : 'text-amber-900'
                    }`}
                  >
                    {availabilityResult.message}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 italic">Select a mode and check availability to see results</p>
                )}
                {availabilitySendError && (
                  <p className="text-sm text-red-600">{availabilitySendError}</p>
                )}
              </div>
            </>
          )}
        </form>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-xs font-semibold px-3 py-1.5 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          {modalTab === 'booking' && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-semibold px-3.5 py-1.5 rounded-full hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting && (
                <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white" />
              )}
              {copy.submit}
            </button>
          )}
        </div>
      </div>

      {showAvailabilityN2 && availabilityResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900">N2 Request / Response</h4>
              <button
                type="button"
                onClick={() => setShowAvailabilityN2(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto p-4">
              <div>
                <h5 className="text-xs font-semibold text-gray-700 mb-2">Request</h5>
                <pre className="bg-gray-900 text-gray-100 text-xs rounded p-3 overflow-auto whitespace-pre-wrap max-h-[60vh]">
                  {formatJsonPayload(availabilityResult.n2RequestJson) || 'No N2 request stored'}
                </pre>
              </div>
              <div>
                <h5 className="text-xs font-semibold text-gray-700 mb-2">Response</h5>
                <pre className="bg-gray-900 text-gray-100 text-xs rounded p-3 overflow-auto whitespace-pre-wrap max-h-[60vh]">
                  {formatJsonPayload(availabilityResult.n2ResponseJson) || 'No N2 response stored'}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualAppointmentModal;
